<template>
  <div id="gallery" class="gallery-container">
    <div class="app-container">
      <!-- Loading Skeleton State -->
      <div v-if="loading" class="gallery-skeleton-grid">
        <div v-for="n in 3" :key="n" class="ui-card">
          <div class="ui-skeleton" style="aspect-ratio: 3/4"></div>
          <div class="ui-card__body stack" style="padding: var(--space-4)">
            <div class="ui-skeleton" style="height: 22px; width: 80%"></div>
            <div class="ui-skeleton" style="height: 14px; width: 50%"></div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!tournaments.length" class="ui-empty">
        <span class="ui-empty__icon"><i class="bi bi-trophy" aria-hidden="true"></i></span>
        <h3 class="ui-empty__title">Belum Ada Turnamen</h3>
        <p>Saat ini belum ada jadwal kejuaraan yang dibuka. Pantau terus halaman ini atau ikuti media sosial kami.</p>
      </div>

      <!-- Single Tournament Showcase (Prevents 70% empty void on desktop when only 1 item exists) -->
      <div v-else-if="tournaments.length === 1" class="t-single-wrapper anim-fade-up">
        <article class="ui-card ui-card--flame t-card-single">
          <div class="t-card-single__poster">
            <router-link :to="{ name: 'GalleryDetail', params: { slug: tournaments[0].slug } }" class="hover-zoom">
              <SmartImage :source="tournaments[0].image" :alt="tournaments[0].name" :label="tournaments[0].name" ratio="3 / 4" />
              <span class="ui-badge t-card__badge" :class="`ui-badge--${infoStatus(tournaments[0]).tone}`">
                {{ $t(infoStatus(tournaments[0]).kunci === 'status.dibuka' ? 'status.dibukaSingkat' : infoStatus(tournaments[0]).kunci) }}
              </span>
            </router-link>
          </div>

          <div class="t-card-single__content">
            <div class="t-card-single__badge-row">
              <span class="eyebrow"><i class="bi bi-fire text-flame"></i> KEJUARAAN UTAMA</span>
            </div>
            <h3 class="t-card-single__title">
              <router-link :to="{ name: 'GalleryDetail', params: { slug: tournaments[0].slug } }">{{ tournaments[0].name }}</router-link>
            </h3>

            <div class="t-card-single__meta-list">
              <div v-if="tournaments[0].location" class="t-card-single__meta-item">
                <div class="t-card-single__meta-icon"><i class="bi bi-geo-alt-fill"></i></div>
                <div>
                  <span class="t-card-single__meta-label">{{ $t('tournament.location') }}</span>
                  <span class="t-card-single__meta-val">{{ tournaments[0].location }}</span>
                </div>
              </div>

              <div v-if="sisaPendaftaran(tournaments[0])" class="t-card-single__meta-item" :class="{ 'is-mendesak': mendesak(tournaments[0]) }">
                <div class="t-card-single__meta-icon"><i class="bi bi-hourglass-split"></i></div>
                <div>
                  <span class="t-card-single__meta-label">{{ $t('tournament.registrationDeadline') }}</span>
                  <span class="t-card-single__meta-val">{{ sisaPendaftaran(tournaments[0]) }}</span>
                </div>
              </div>
            </div>

            <div class="t-card-single__actions">
              <router-link
                v-if="tournaments[0].bisa_daftar || infoStatus(tournaments[0]).bisaDaftar"
                :to="{ name: 'registration', params: { slug: tournaments[0].slug } }"
                class="ui-btn ui-btn--primary ui-btn--md"
              >
                <i class="bi bi-pencil-square" aria-hidden="true"></i>
                {{ $t('nav.register') }}
              </router-link>
              <router-link
                :to="{ name: 'GalleryDetail', params: { slug: tournaments[0].slug } }"
                class="ui-btn ui-btn--secondary ui-btn--md hover-arrow"
              >
                Lihat Detail & Bagan
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
              </router-link>
            </div>
          </div>
        </article>
      </div>

      <!-- Multiple Tournaments Grid -->
      <div v-else class="card-grid">
        <article
          v-for="tournament in tournaments"
          :key="tournament.id"
          class="ui-card ui-card--interactive ui-card--flame t-card"
        >
          <router-link
            :to="{ name: 'GalleryDetail', params: { slug: tournament.slug } }"
            class="t-card__link hover-zoom"
          >
            <SmartImage
              :source="tournament.image"
              :alt="tournament.name"
              :label="tournament.name"
              ratio="3 / 4"
            />
            <span class="ui-badge t-card__badge" :class="`ui-badge--${infoStatus(tournament).tone}`">
              {{ $t(infoStatus(tournament).kunci === 'status.dibuka' ? 'status.dibukaSingkat' : infoStatus(tournament).kunci) }}
            </span>
          </router-link>

          <div class="ui-card__body">
            <h3 class="t-card__title">
              <router-link :to="{ name: 'GalleryDetail', params: { slug: tournament.slug } }">
                {{ tournament.name }}
              </router-link>
            </h3>
            <p v-if="tournament.location" class="t-card__meta">
              <i class="bi bi-geo-alt" aria-hidden="true"></i>{{ tournament.location }}
            </p>
            <p
              v-if="sisaPendaftaran(tournament)"
              class="t-card__meta t-card__tenggat"
              :class="{ 'is-mendesak': mendesak(tournament) }"
            >
              <i class="bi bi-hourglass-split" aria-hidden="true"></i>{{ sisaPendaftaran(tournament) }}
            </p>
          </div>

          <div class="t-card__actions">
            <router-link
              v-if="tournament.bisa_daftar || infoStatus(tournament).bisaDaftar"
              :to="{ name: 'registration', params: { slug: tournament.slug } }"
              class="ui-btn ui-btn--primary ui-btn--sm flex-grow-1"
            >
              <i class="bi bi-pencil-square"></i> {{ $t('nav.register') }}
            </router-link>
            <router-link
              :to="{ name: 'GalleryDetail', params: { slug: tournament.slug } }"
              class="ui-btn ui-btn--secondary ui-btn--sm flex-grow-1"
            >
              Detail
            </router-link>
          </div>
        </article>
      </div>
    </div> 
  </div>
</template>

<script>
import http from '@/services/http';
import SmartImage from '@/components/ui/SmartImage.vue';
import { infoStatus } from '@/utils/status';

export default {
  name: 'TournamentGallery',
  components: {
    SmartImage,
  },
  data() {
    return {
      tournaments: [],
      loading: true,
    };
  },
  mounted() {
    this.getTournaments();
  },
  methods: {
    infoStatus,

    sisaPendaftaran(t) {
      // Saat pendaftaran tertutup (admin, waktu habis, kuota penuh) hitungan mundur tidak relevan.
      if (!infoStatus(t).bisaDaftar) return '';
      const n = t?.sisa_hari_pendaftaran;
      if (n === null || n === undefined) return '';
      if (n < 0) return this.$t('tournament.deadlinePassed');
      if (n === 0) return this.$t('tournament.deadlineToday');
      return this.$t('tournament.deadlineLeft', { n });
    },

    mendesak(t) {
      const n = t?.sisa_hari_pendaftaran;
      return typeof n === 'number' && n >= 0 && n <= 7;
    },

    async getTournaments() {
      this.loading = true;
      try {
        const response = await http.get('/tournaments/gallery');
        this.tournaments = response.data.data || [];
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
        this.tournaments = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.gallery-container {
  width: 100%;
}

.gallery-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
}

@media (min-width: 900px) {
  .gallery-skeleton-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-6);
  }
}

/* ══════════════════════ KARTU TURNAMEN TUNGGAL ══════════════════════ */
.t-single-wrapper {
  max-width: 380px;
  margin-inline: auto;
}

.t-card-single {
  display: grid;
  grid-template-columns: 1fr;
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-2xl);
  background: var(--bg-surface);
  box-shadow: var(--shadow-md);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.t-card-single:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
}

.t-card-single__poster {
  position: relative;
  overflow: hidden;
}

.t-card-single__content {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/* Satu kolom: poster di atas, info dan tombol di bawahnya. */

.t-card-single__title {
  font-size: var(--text-lg, 1.125rem);
  margin: 0;
  font-weight: 700;
  line-height: 1.3;
}

.t-card-single__title a {
  color: var(--text-primary);
  text-decoration: none;
}

.t-card-single__title a:hover {
  color: var(--text-brand);
}

.t-card-single__meta-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.t-card-single__meta-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-sm);
}

.t-card-single__meta-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.t-card-single__meta-label {
  display: block;
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.t-card-single__meta-val {
  display: block;
  font-weight: 600;
  color: var(--text-primary);
}

.t-card-single__actions {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-2);
  margin-top: var(--space-1);
}
.t-card-single__actions .ui-btn { justify-content: center; font-size: var(--text-sm); padding-inline: var(--space-3); white-space: nowrap; }
.t-card-single__actions .ui-btn:only-child { grid-column: 1 / -1; }
.t-card-single__badge-row .eyebrow { font-size: 0.7rem; }
.t-card-single__meta-icon { width: 30px; height: 30px; font-size: 0.95rem; }

/* ══════════════════════ KARTU REGULER ══════════════════════ */
.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-3);
}

@media (min-width: 560px)  { .card-grid { gap: var(--space-5); } }
@media (min-width: 900px)  { .card-grid { grid-template-columns: repeat(3, 1fr); gap: var(--space-6); } }
@media (min-width: 1200px) { .card-grid { grid-template-columns: repeat(4, 1fr); } }

.t-card { display: flex; flex-direction: column; }
.t-card__link { position: relative; display: block; }

.t-card__badge {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  z-index: 2;
  background: var(--bg-surface);
  box-shadow: var(--shadow-md);
}

.t-card .ui-card__body { flex: 1; }
.t-card__title {
  font-size: var(--text-base);
  margin: 0 0 var(--space-2);
  overflow-wrap: anywhere;
}

@media (min-width: 768px) { .t-card__title { font-size: var(--text-lg); } }

.t-card__title a { color: var(--text-primary); }
.t-card__title a:hover { color: var(--text-brand); }

.t-card__meta {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: 1.4;
}

.t-card__meta i { margin-top: 2px; flex-shrink: 0; color: var(--accent); }

@media (min-width: 768px) { .t-card__meta { font-size: var(--text-sm); } }

.t-card__tenggat { color: var(--text-muted); }
.t-card__tenggat.is-mendesak { color: var(--danger-600); font-weight: 700; }

.t-card__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--border-subtle);
}

.t-card__actions .ui-btn {
  width: 100%;
  min-height: 34px;
  padding: 6px 8px;
  font-size: 0.8125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .t-card__actions {
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-5);
  }
  .t-card__actions .ui-btn {
    flex: 1;
    min-width: 0;
    font-size: var(--text-sm);
    min-height: 38px;
  }
}
</style>